export const COMPANY_LIST_DEFAULT_PAGE_SIZE = 10;
export const COMPANY_LIST_MAX_PAGE_SIZE = 50;

export function normalizeCompanyPagination(page?: number, take?: number) {
  const normalizedPage =
    Number.isFinite(page) && (page ?? 0) > 0 ? Math.floor(page as number) : 1;
  const requestedTake =
    Number.isFinite(take) && (take ?? 0) > 0
      ? Math.floor(take as number)
      : COMPANY_LIST_DEFAULT_PAGE_SIZE;

  return {
    page: normalizedPage,
    take: Math.min(requestedTake, COMPANY_LIST_MAX_PAGE_SIZE),
  };
}

export function isCompanyObjectIdCursor(cursor?: string): cursor is string {
  return typeof cursor === "string" && /^[a-f\d]{24}$/i.test(cursor);
}

/** Stored object ids are lowercase. String range checks are case-sensitive. */
export function normalizeCompanyCursor(cursor?: string): string | undefined {
  if (!isCompanyObjectIdCursor(cursor)) return undefined;
  return cursor.toLowerCase();
}

export type CompanyIdRow = { id: string };

/**
 * Merge two id-ascending company lists. Callers must pass each side already
 * sorted ascending; the companies list orders exhibitors and sponsors together
 * by id.
 */
export function mergeCompaniesById<T extends CompanyIdRow>(
  exhibitors: readonly T[],
  sponsors: readonly T[]
): T[] {
  const merged: T[] = [];
  let exhibitorIndex = 0;
  let sponsorIndex = 0;
  while (exhibitorIndex < exhibitors.length && sponsorIndex < sponsors.length) {
    if (exhibitors[exhibitorIndex].id <= sponsors[sponsorIndex].id) {
      merged.push(exhibitors[exhibitorIndex]);
      exhibitorIndex += 1;
    } else {
      merged.push(sponsors[sponsorIndex]);
      sponsorIndex += 1;
    }
  }
  while (exhibitorIndex < exhibitors.length) {
    merged.push(exhibitors[exhibitorIndex]);
    exhibitorIndex += 1;
  }
  while (sponsorIndex < sponsors.length) {
    merged.push(sponsors[sponsorIndex]);
    sponsorIndex += 1;
  }
  return merged;
}

export type CompanyWindowPlan = {
  direction: "forward" | "backward";
  /**
   * How many ids to read from each collection. Forward reads the smallest
   * ids; backward reads the largest. The merged page is contained in those
   * two windows, so neither side is scanned to the end of the event.
   */
  fetchPerSide: number;
  /** Forward only: ids to drop from the start of the merged window. */
  skipInMerged: number;
  limit: number;
};

/**
 * Choose a forward or backward id window for an offset page.
 *
 * The first `offset + pageSize` merged ids live in the first `offset +
 * pageSize` ids of each collection. The last `fromEnd + pageSize` merged ids
 * live in the largest `fromEnd + pageSize` ids of each collection. Reading the
 * smaller of those two windows keeps the first page and the last page the
 * same size.
 */
export function planCompanyIdWindow(input: {
  total: number;
  offset: number;
  limit: number;
}): CompanyWindowPlan | null {
  const { total, offset, limit } = input;
  if (
    limit <= 0 ||
    offset < 0 ||
    !Number.isFinite(total) ||
    !Number.isFinite(offset) ||
    !Number.isFinite(limit)
  ) {
    return null;
  }
  if (offset >= total || total <= 0) return null;

  const pageSize = Math.min(limit, total - offset);
  const fromEnd = total - offset - pageSize;
  const forwardFetch = offset + pageSize;
  const backwardFetch = fromEnd + pageSize;

  if (backwardFetch < forwardFetch) {
    return {
      direction: "backward",
      fetchPerSide: backwardFetch,
      skipInMerged: 0,
      limit: pageSize,
    };
  }

  return {
    direction: "forward",
    fetchPerSide: forwardFetch,
    skipInMerged: offset,
    limit: pageSize,
  };
}

/**
 * Slice one page out of the two id windows.
 *
 * `exhibitors` and `sponsors` must be in query order: ascending for a forward
 * plan, descending for a backward plan (Mongo `orderBy id desc`).
 */
export function pageFromCompanyIdSides<T extends CompanyIdRow>(
  exhibitors: readonly T[],
  sponsors: readonly T[],
  plan: CompanyWindowPlan
): T[] {
  const exhibitorAsc =
    plan.direction === "backward" ? [...exhibitors].reverse() : exhibitors;
  const sponsorAsc =
    plan.direction === "backward" ? [...sponsors].reverse() : sponsors;
  const merged = mergeCompaniesById(exhibitorAsc, sponsorAsc);

  if (plan.direction === "forward") {
    return merged.slice(plan.skipInMerged, plan.skipInMerged + plan.limit);
  }

  // `merged` is the largest `fetchPerSide` ids of each side, so the global
  // tail is the last `fetchPerSide` of that merge. The page is the start of
  // that tail; the rest sit strictly after this page.
  const tail = merged.slice(-plan.fetchPerSide);
  return tail.slice(0, plan.limit);
}

/** Cursor page: each side is the next `limit + 1` ids strictly after the cursor. */
export function companyCursorPage<T extends CompanyIdRow>(
  exhibitors: readonly T[],
  sponsors: readonly T[],
  limit: number
): { page: T[]; hasNextPage: boolean; nextCursor: string | null } {
  const merged = mergeCompaniesById(exhibitors, sponsors);
  const hasNextPage = merged.length > limit;
  const page = hasNextPage ? merged.slice(0, limit) : merged;
  return {
    page,
    hasNextPage,
    nextCursor: hasNextPage ? page[page.length - 1]?.id ?? null : null,
  };
}

export function companyListPage<T>(args: {
  data: T[];
  total: number;
  page: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  nextCursor: string | null;
}) {
  return {
    data: args.data,
    pagination: {
      total: args.total,
      hasNextPage: args.hasNextPage,
      hasPreviousPage: args.hasPreviousPage,
      currentPage: args.page,
      nextCursor: args.nextCursor,
    },
  };
}

export function emptyCompanyListPage<T>(
  page: number,
  total = 0,
  hasPreviousPage = page > 1
) {
  return companyListPage<T>({
    data: [],
    total,
    page,
    hasNextPage: false,
    hasPreviousPage,
    nextCursor: null,
  });
}

/**
 * Page a single id-sorted list. Search uses this after the matching ids are
 * known. Cursor pages keep `total` as the full match count.
 */
export function pageSortedCompanyIds(
  ids: readonly string[],
  args: { page: number; take: number; cursor?: string }
) {
  const total = ids.length;
  const source = args.cursor
    ? ids.filter((id) => id > (args.cursor as string))
    : ids;
  const offset = args.cursor ? 0 : Math.max(0, (args.page - 1) * args.take);
  const page = source.slice(offset, offset + args.take);
  const hasNextPage = offset + page.length < source.length;
  return {
    ids: page,
    total,
    hasNextPage,
    nextCursor: hasNextPage ? page[page.length - 1] ?? null : null,
  };
}

/**
 * Escape regex metacharacters so Prisma/Mongo `contains` stays a literal
 * substring, matching the in-memory card filter (`String.includes`).
 */
export function escapeCompanySearchTerm(query: string): string {
  return query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Whitespace-only input is not a search; callers keep the unfiltered path. */
export function normalizeCompanySearchQuery(query?: string | null): string {
  return query?.trim() ?? "";
}

/**
 * Merge unique company ids in ascending order. Search builds one id window from
 * email hits, language-aware name hits, and profile-fallback hits.
 */
export function mergeUniqueCompanyIds(
  ...groups: ReadonlyArray<ReadonlyArray<string>>
): string[] {
  const seen = new Set<string>();
  const merged: string[] = [];
  for (const group of groups) {
    for (const id of group) {
      if (!id || seen.has(id)) continue;
      seen.add(id);
      merged.push(id);
    }
  }
  merged.sort((left, right) => (left < right ? -1 : left > right ? 1 : 0));
  return merged;
}

/**
 * Event-scoped search `where` for `loadMergedCompanyWindow`.
 *
 * The predicate is an embedded `searchCards` elemMatch (card name + email),
 * not an `id: { in: every match }` list and not a `translations` relation
 * filter. The requested language is preferred; "*" is the first-translation
 * fallback and applies only when that language has no card.
 */
export function buildCompanySearchCollectionWhere(input: {
  eventId: string;
  groupIds: readonly string[];
  lang: string;
  textContains: string;
}): {
  eventId: string;
  participantGroupId: { in: string[] };
  OR: Array<
    | {
        searchCards: {
          some: {
            lang: string;
            text: { contains: string; mode: "insensitive" };
          };
        };
      }
    | {
        AND: [
          { NOT: { searchCards: { some: { lang: string } } } },
          {
            searchCards: {
              some: {
                lang: "*";
                text: { contains: string; mode: "insensitive" };
              };
            };
          },
        ];
      }
  >;
} | null {
  if (!input.groupIds.length || !input.textContains) return null;
  const lang = input.lang.trim() || "en";
  const text = {
    contains: input.textContains,
    mode: "insensitive" as const,
  };
  return {
    eventId: input.eventId,
    participantGroupId: { in: [...input.groupIds] },
    OR: [
      { searchCards: { some: { lang, text } } },
      {
        AND: [
          { NOT: { searchCards: { some: { lang } } } },
          {
            searchCards: {
              some: { lang: "*", text },
            },
          },
        ],
      },
    ],
  };
}
